import React from 'react';

import CaretRightSmallIcon from 'components/common/IconsV2/CaretRightSmallIcon';
import { cn } from 'helpers/classnames';
import { useFilteredSortOptions } from 'hooks/useFilteredSortOptions';
import { useSortSelectedValue } from 'hooks/useSortSelectedValue';

import styles from 'styles/components/search/sort.scss';

interface SortProps {
  sortId?: string;
  testId?: string;
  className?: string;
  accordion?: boolean;
  filters?: any;
  selectedSort?: Record<string, string>;
  onSortSelected?: React.ChangeEventHandler<HTMLSelectElement>;
}

export interface SortOptionProps {
  label: string;
  value: string;
  eventLabel?: string;
  ariaLabel?: string;
}

/**
 * Given an array of sort options, render option elements
 * @param  {object[]} optionData options to render
 * @return {object[]}            rendered options
 */
export const makeSortOptions = (optionData: SortOptionProps[], showBestForYouSort: boolean) =>
  optionData
    // Filters out Best For You if needed
    .filter(({ label }) => label !== 'Best For You' || (label === 'Best For You' && showBestForYouSort))
    // Renders the options
    .map(({ eventLabel, label, value, ariaLabel }) => (
      <option key={value} value={value} aria-label={ariaLabel || label} data-label={eventLabel || label}>
        {label}
      </option>
    ));

const Sort = (props: SortProps) => {
  const {
    testId,
    className,
    accordion,
    selectedSort,
    onSortSelected,
    sortId = 'searchSort',
    filters: { bestForYouSortEligible }
  } = props;
  // Parse the options
  const parsedSortOptions = useFilteredSortOptions();

  // Get the selected value
  const selectedValue = useSortSelectedValue(parsedSortOptions, selectedSort);

  // Makes the sort options element
  const SortOptions = makeSortOptions(parsedSortOptions, !!bestForYouSortEligible);

  return (
    <div className={cn(styles.sort, { className })}>
      {accordion ? (
        <div>{SortOptions}</div>
      ) : (
        <span>
          <label className="zappos:inline-flex zappos:items-center" htmlFor={sortId}>
            Sort <span className="zappos:ml-1">By</span>
            <CaretRightSmallIcon className="ml-1 rotate-90 md:hidden" size={16} />
          </label>
          <select id={sortId} className={styles.sortSelect} value={selectedValue?.value} onChange={onSortSelected} data-test-id={testId}>
            {SortOptions}
          </select>
        </span>
      )}
    </div>
  );
};

export default Sort;
